import { Button } from '@/components/ui/button'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Header from '@/components/Header'
import authBg from '@/assets/Landing/authbg.svg'
import { useAuth } from '@/context/AuthContext'
import { getRedirectPath } from '@/utils/auth-redirect'

export default function CreatePasswordPage() {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [userId, setUserId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { createPassword, loading, user } = useAuth()

  useEffect(() => {
    const state = location.state as { userId?: string } | null
    if (state?.userId) setUserId(state.userId)
  }, [location.state])

  useEffect(() => {
    if (user) {
      const redirectPath = getRedirectPath(user)
      navigate(redirectPath, { replace: true })
    }
  }, [user, navigate])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    createPassword({ userId, password: newPassword, confirmPassword })
      .then(() => {
        setMessage('Password created successfully. Redirecting...')
        // Navigation will be handled by the useEffect watching user state
      })
      .catch(err => {
        setError((err as Error)?.message || 'Unable to create password')
      })
  }

  const passwordRequirements = {
    hasMinLength: newPassword.length >= 8,
    hasNumber: /\d/.test(newPassword),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  }

  const isPasswordValid = Object.values(passwordRequirements).every(req => req)

  return (
    <div className="min-h-screen bg-white flex flex-col w-full">
      <Header />

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:grid lg:grid-rows-2 w-full">
        {/* Left Section - Form */}
        <div className="flex flex-col lg:flex-row items-center justify-center px-6 lg:px-16 py-12 lg:py-16 w-full">
          <div className="w-full space-y-8 flex flex-col lg:flex-row gap-8">
            <div className="space-y-3 flex flex-col justify-start items-start lg:w-1/2">
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900">
                Create Your New Password
              </h2>
              <div className="space-y-1">
                <p className="text-gray-600 text-base lg:text-lg xl:text-xl text-start">
                  For your security, please create a new password before continuing. This ensures that only you can access your account.
                </p>
              </div>
            </div>

           <div className='flex flex-row items-center justify-center w-full lg:w-1/2'>
             <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-md">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-start">
                  {error}
                </div>
              )}
              {message && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm text-start">
                  {message}
                </div>
              )}
              {/* New Password Field */}
              <div className="space-y-2">
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 text-start">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••••"
                    className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-900 placeholder:text-gray-400"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 text-start">
                  Confirm New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••••"
                    className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-300 outline-none text-gray-900 placeholder:text-gray-400"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password Requirements */}
              <div className="text-xs text-gray-600 space-y-1">
                <p className="text-start">
                  Password must be at least <span className="font-medium text-gray-900">8 characters</span>, 
                  and include <span className="font-medium text-gray-900">one number</span> and{' '}
                  <span className="font-medium text-gray-900">one special symbol</span> (e.g., !, @, #, $).
                </p>
              </div>

              {/* Save Password Button */}
              <Button
                type="submit"
                disabled={!isPasswordValid || newPassword !== confirmPassword || loading}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-3 text-base font-medium transition-all disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Save New Password
              </Button>
            </form>
           </div>
          </div>
        </div>

        {/* Right Section - Illustration */}
        <div className="lg:flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100/50 relative overflow-hidden rounded-tr-[32px] rounded-tl-[32px] mx-4 lg:mx-20 h-64 lg:h-auto min-h-[300px] lg:min-h-[400px]">
          <img 
            src={authBg} 
            alt="Authentication illustration" 
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
      </div>
    </div>
  )
}
